import {
  useContext,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEye,
  faPencil,
  faCheck,
  faTrash,
  faGripDotsVertical,
} from '@fortawesome/pro-solid-svg-icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  useAppDispatch,
  useAppSelector,
  activeImageSelector,
  setActiveImage,
  activeSpaceSelector,
  setActiveSpace,
  setActiveModel,
  isEditingSelector,
  editModeSelector,
  spacePositionsInAllLinkedLayersSelector,
  updateSpace,
  removeSpaceInImage,
  type SpaceType,
  setEditedSpace,
  setMarkerEditingSpaceId,
} from '../../../../../store';
import { LeafletContext } from '../../../../../App';

import './SpaceListItem.scss';

export interface SpaceListItemProps {
  space: SpaceType;
  categoryIndex: number;
}

export function SpaceListItem({ space, categoryIndex }: SpaceListItemProps) {
  const { mapRef, activeMarkerRef, hoveredMarker, setHoveredMarker } = useContext(LeafletContext);

  const dispatch = useAppDispatch();
  const activeImage = useAppSelector(activeImageSelector);
  const spacePositions = useAppSelector(spacePositionsInAllLinkedLayersSelector);
  const isEditing = useAppSelector(isEditingSelector);
  const editMode = useAppSelector(editModeSelector);
  const position = useAppSelector(spacePositionsInAllLinkedLayersSelector)[space.id];
  const active = useAppSelector(activeSpaceSelector);
  const setActive = (s: SpaceType) => {
    dispatch(setActiveSpace(s.id));
    dispatch(setActiveModel(s.vr_model));
  };
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: space.id,
    data: {
      type: 'space',
      space: space,
      categoryIndex: categoryIndex,
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const [renaming, setRenaming] = useState(false);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  const jumpToMarker = () => {
    const marker = activeMarkerRef.current[space.id];
    if (!marker) return;
    const popup = marker.getPopup();
    if (popup) {
      marker.openPopup();
    } else {
      // Markers without popup (e.g. spaces shown as overlays) handle their click
      marker.fire('click');
    }
    setActive(space);

    mapRef.current!.stop();
    mapRef.current!.flyTo([position.x, position.y], 19);
    if (popup) {
      // Center popup after flying to marker
      mapRef.current!.once(
        'zoomend',
        () => mapRef.current!.fire('popupopen', { popup })
      );
    }
    setActive(space);
  };

  if (isDragging) {
    return (
      <li
        ref={setNodeRef}
        style={style}
        className='space-placeholder'
      />
    );
  }

  return (
    <li
      className={cx('space', {
        active: active?.id === space.id,
        disabled: !spacePositions[space.id],
        'hover': hoveredMarker === space.id,
      })}
      key={space.id}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={() => {
        if (isEditing) {
          if (editMode === 'marker') {
            dispatch(setMarkerEditingSpaceId(space.id));
          } else {
            dispatch(setEditedSpace({
              id: space.id,
              position: { lat: +spacePositions[space.id].x, lng: +spacePositions[space.id].y, layerId: spacePositions[space.id].layerId },
            }));
          }
          mapRef.current?.stop();
          mapRef.current?.panTo([+spacePositions[space.id].x, +spacePositions[space.id].y]);
          return;
        }
        // Linked layers (shared markers)
        const isDuplicateLayer = spacePositions[space.id]?.layerId === activeImage?.duplicateOf;
        if (!isDuplicateLayer && spacePositions[space.id]?.layerId !== activeImage?.id) {
          // Dependent layers (floors)
          dispatch(setActiveImage(spacePositions[space.id].layerId));
          // TODO: Find better way to change layers
          // this makes some visual glitches
          setTimeout(jumpToMarker, 300);
        } else {
          jumpToMarker();
        }
      }}
      onMouseEnter={() => setHoveredMarker(space.id)}
      onMouseLeave={() => setHoveredMarker('')}
    >
      {space.visual || space.visual_uploaded?.base64 ? (
        <img
          className="thumbnail"
          src={space.visual || space.visual_uploaded.base64}
          alt={space.name}
        />
      ) : (
        <span className="no-image">
          <FontAwesomeIcon icon={faEye} />
        </span>
      )}
      <span className={cx('space-title', { 'hidden': renaming })}>
        {space.name}
      </span>
      {renaming && (
        <input
          ref={renameInputRef}
          className='rename-space'
          defaultValue={space.name}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              setRenaming(false);
              dispatch(updateSpace({
                ...space,
                name: renameInputRef.current!.value
              }));
            }
          }}
        />
      )}
      {isEditing && (
        <div className="edit-buttons">
          <FontAwesomeIcon
            icon={faTrash}
            role="button"
            onClickCapture={() => {
              dispatch(removeSpaceInImage(space.id));
            }}
          />
          <FontAwesomeIcon
            icon={renaming ? faCheck : faPencil}
            role="button"
            onClickCapture={(e) => {
              e.stopPropagation();
              if (renaming) {
                setRenaming(false);
                dispatch(updateSpace({
                  ...space,
                  name: renameInputRef.current!.value
                }));
              } else {
                setRenaming(true);
              }
            }}
          />
          <FontAwesomeIcon
            icon={faGripDotsVertical}
            role="button"
          />
        </div>
      )}
    </li>
  );
}
